/**
 * 权限管理 · 角色管理（canvas page-85 对齐 · RBACROLE-1 样稿 rbac-roles-mockup-v1 画面 1/2/5）
 * StatRow 4（角色总数 内置 N + 自定义 M / 用户总数 / 权限点总数 / 本月权限变更）
 * + 自定义角色一栏（排在内置前面：标「自定义」、可改名、可删除）+ 内置角色卡（不可删不可改名）
 * + 「＋ 新建角色」弹窗（名称 / 说明 / 从空白或复制某角色开始 → 创建后跳权限矩阵聚焦勾选）
 * API：GET/POST /api/v1/rbac/roles · PATCH/DELETE /api/v1/rbac/roles/{code}（见 designs/mockups/rbac-roles/API_MAP.md）
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Shield, Users, KeySquare, History, ChevronRight, Plus, X } from "lucide-react";
import { rbacApi, type RbacRole, type RoleInUseUser } from "@/services/rbac";
import { ApiError } from "@/services/api";
import { toFrontendRole } from "@/services/types/auth";
import { Can } from "@/auth/permissions";
import { PageShell, Pill, LoadingState, ErrorState } from "@/shared/ui/PageHeader";
import { RbacTabs } from "./RbacTabs";
import { ROLE_META, roleEmoji } from "./rbacMeta";

const MANAGE_PERM = "auth.roles.assign";

export function RolesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const rolesQ = useQuery({ queryKey: ["rbac", "roles"], queryFn: rbacApi.listRoles });
  const usersQ = useQuery({ queryKey: ["rbac", "users"], queryFn: rbacApi.listUsers });
  const permsQ = useQuery({ queryKey: ["rbac", "permissions"], queryFn: rbacApi.listPermissions });
  const auditQ = useQuery({ queryKey: ["rbac", "audit", "all"], queryFn: () => rbacApi.auditLogs() });

  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<RbacRole | null>(null);
  const [deleting, setDeleting] = useState<RbacRole | null>(null);

  const roles = rolesQ.data ?? [];
  const users = usersQ.data ?? [];
  const perms = permsQ.data ?? [];
  const audits = auditQ.data ?? [];

  const custom = useMemo(() => roles.filter((r) => r.kind === "custom"), [roles]);
  const builtin = useMemo(() => {
    const order = Object.keys(ROLE_META);
    const idx = (code: string) => order.indexOf(toFrontendRole(code));
    return roles.filter((r) => r.kind !== "custom").sort((a, b) => idx(a.code) - idx(b.code));
  }, [roles]);

  const changesThisMonth = useMemo(() => {
    const ym = new Date().toISOString().slice(0, 7);
    return audits.filter((a) => (a.occurred_at ?? "").slice(0, 7) === ym).length;
  }, [audits]);

  if (rolesQ.isLoading) return <LoadingState />;
  if (rolesQ.isError) return <ErrorState message="角色列表加载失败" onRetry={() => rolesQ.refetch()} />;

  return (
    <PageShell
      title="角色管理"
      subtitle="内置角色 + 自定义角色 · 司导仅通知不接入系统"
      actions={
        <Can perm={MANAGE_PERM}>
          <button
            type="button"
            data-testid="new-role-btn"
            onClick={() => setCreating(true)}
            className="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white hover:bg-blue-700"
          >
            <Plus className="h-3.5 w-3.5" /> 新建角色
          </button>
        </Can>
      }
    >
      <RbacTabs />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <RStat icon={<Shield className="h-4 w-4" />} tone="indigo" label="系统角色总数" value={roles.length} hint={`内置 ${builtin.length} + 自定义 ${custom.length}`} />
        <RStat icon={<Users className="h-4 w-4" />} tone="emerald" label="系统用户总数" value={users.length} hint="员工 + 同业 B2B" />
        <RStat icon={<KeySquare className="h-4 w-4" />} tone="amber" label="权限点总数" value={perms.length} hint={`${new Set(perms.map((p) => p.module)).size} 模块`} />
        <RStat icon={<History className="h-4 w-4" />} tone="slate" label="本月权限变更" value={changesThisMonth} hint="次" link="/admin/permissions/audit-logs" linkLabel="查看审计日志" />
      </div>

      {custom.length > 0 && (
        <>
          <p className="text-[12px] text-slate-500" data-testid="custom-section-title">
            自定义角色（{custom.length} 个 · 老板 / 技术管理员建 · 可改名 · 可删除）
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {custom.map((r) => (
              <RoleCard key={r.code} r={r} custom onRename={() => setRenaming(r)} onDelete={() => setDeleting(r)} />
            ))}
          </div>
        </>
      )}

      <p className="text-[12px] text-slate-500">内置角色（{builtin.length} 个 · 可编辑权限矩阵 · 不可删）· 系统初始化时创建</p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {builtin.map((r) => (
          <RoleCard key={r.code} r={r} />
        ))}
      </div>

      {creating && (
        <NewRoleModal
          roles={roles}
          onClose={() => setCreating(false)}
          onCreated={(role) => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ["rbac", "roles"] });
            navigate(`/admin/permissions/matrix?role=${role.code}`); // 创建后直接进权限矩阵勾选（画面 2 → 3）
          }}
        />
      )}
      {renaming && (
        <RenameModal
          role={renaming}
          onClose={() => setRenaming(null)}
          onSaved={() => { setRenaming(null); qc.invalidateQueries({ queryKey: ["rbac", "roles"] }); }}
        />
      )}
      {deleting && (
        <DeleteModal
          role={deleting}
          holders={users.flatMap((u): RoleInUseUser[] =>
            u.primary_role === deleting.code ? [{ id: u.id, full_name: u.name, as: "primary" }]
              : u.extra_roles.includes(deleting.code) ? [{ id: u.id, full_name: u.name, as: "additional" }] : [])}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); qc.invalidateQueries({ queryKey: ["rbac", "roles"] }); }}
        />
      )}
    </PageShell>
  );
}

function RoleCard({ r, custom, onRename, onDelete }: { r: RbacRole; custom?: boolean; onRename?: () => void; onDelete?: () => void }) {
  const desc = custom ? r.description : ROLE_META[r.code as keyof typeof ROLE_META]?.desc;
  return (
    <section data-testid={`role-card-${r.code}`} className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{custom ? "🧩" : roleEmoji(r.code)}</span>
          <div>
            <h3 className="text-[15px] font-semibold text-slate-900">{r.label_cn}</h3>
            <code className="text-[11px] text-slate-400">{r.code}</code>
          </div>
        </div>
        <Pill tone={custom ? "info" : "muted"}>{custom ? "自定义" : "内置"}</Pill>
      </div>
      <div className="mt-3 flex items-center gap-4 text-[13px]">
        <span className="text-slate-600"><b className="text-slate-900">{r.user_count ?? 0}</b> 人</span>
        <span className="text-slate-600"><b className="text-slate-900">{r.permission_codes.length}</b> 权限点</span>
      </div>
      {desc && <p className="mt-1 text-[12px] text-slate-500">{desc}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Link
          to={`/admin/permissions/matrix?role=${r.code}`}
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-[12px] text-slate-700 hover:bg-slate-50"
        >
          查看权限 <ChevronRight className="h-3 w-3" />
        </Link>
        {r.code === "tech_admin" ? (
          <span data-testid="fixed-role-tag" title="技术管理员固定拥有全部权限，不能修改" className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-[12px] text-slate-500">🔒 固定全部权限</span>
        ) : (
          <Link
            to={`/admin/permissions/matrix?role=${r.code}`}
            title="到权限矩阵聚焦编辑该角色权限"
            className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-[12px] font-medium text-blue-700 hover:bg-blue-100"
          >
            编辑权限
          </Link>
        )}
        {custom && (
          <Can perm={MANAGE_PERM}>
            <button type="button" data-testid={`rename-${r.code}`} onClick={onRename} className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-[12px] text-slate-700 hover:bg-slate-50">改名</button>
            <button type="button" data-testid={`delete-${r.code}`} onClick={onDelete} className="rounded-md border border-rose-200 bg-white px-3 py-1.5 text-[12px] text-rose-600 hover:bg-rose-50">删除</button>
          </Can>
        )}
      </div>
    </section>
  );
}

function ModalShell({ title, onClose, children, footer }: { title: string; onClose: () => void; children: React.ReactNode; footer: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        role="dialog"
        aria-label={title}
        className="flex w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <button type="button" aria-label="关闭" onClick={onClose}><X className="h-4 w-4 text-slate-400 hover:text-slate-600" /></button>
        </div>
        <div className="space-y-4 px-6 py-5">{children}</div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">{footer}</div>
      </div>
    </div>
  );
}

const inputCls = "h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]";
const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "操作失败 · 请重试");

function NewRoleModal({ roles, onClose, onCreated }: { roles: RbacRole[]; onClose: () => void; onCreated: (r: RbacRole) => void }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [mode, setMode] = useState<"blank" | "copy">("blank");
  const [from, setFrom] = useState("");
  const m = useMutation({
    mutationFn: () => rbacApi.createRole({ label_cn: name.trim(), description: desc.trim() || undefined, copy_from_code: mode === "copy" ? from : null }),
    onSuccess: onCreated,
  });
  const ready = name.trim().length > 0 && (mode === "blank" || from !== "");
  return (
    <ModalShell
      title="新建角色"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-[13px]">取消</button>
          <button type="button" data-testid="create-role-submit" disabled={!ready || m.isPending} onClick={() => m.mutate()} className="h-9 rounded-lg bg-blue-600 px-5 text-[13px] font-semibold text-white disabled:opacity-50">
            {m.isPending ? "创建中…" : "创建并去勾选权限"}
          </button>
        </>
      }
    >
      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-medium text-slate-700">角色名称 *</span>
        <input data-testid="role-name" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="例如：车辆管理员" className={inputCls} />
        <span className="text-[11px] text-slate-400">同事能看懂的岗位名；不能和已有角色重名</span>
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-medium text-slate-700">说明</span>
        <input data-testid="role-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="例如：只管车辆、证照、维修和车务台账" className={inputCls} />
      </label>
      <div>
        <p className="mb-1.5 text-[12px] font-medium text-slate-700">从哪里开始</p>
        <div className="grid grid-cols-1 gap-1.5">
          <label className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-[13px] ${mode === "blank" ? "border-blue-300 bg-blue-50 ring-1 ring-blue-200" : "border-slate-200 bg-white"}`}>
            <input type="radio" name="start" checked={mode === "blank"} onChange={() => setMode("blank")} className="mt-0.5" />
            <span><b className="text-slate-800">空白</b><span className="block text-[11px] text-slate-400">一个权限都没有，下一步自己勾</span></span>
          </label>
          <label className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-[13px] ${mode === "copy" ? "border-blue-300 bg-blue-50 ring-1 ring-blue-200" : "border-slate-200 bg-white"}`}>
            <input type="radio" name="start" checked={mode === "copy"} onChange={() => setMode("copy")} className="mt-0.5" />
            <span className="min-w-0 flex-1">
              <b className="text-slate-800">复制现有角色</b><span className="block text-[11px] text-slate-400">先带上它的全部权限，再删减</span>
              {mode === "copy" && (
                <select data-testid="copy-from" value={from} onChange={(e) => setFrom(e.target.value)} className={`${inputCls} mt-2`}>
                  <option value="">选一个角色</option>
                  {roles.map((r) => <option key={r.code} value={r.code}>{r.label_cn}（{r.permission_codes.length} 权限点）</option>)}
                </select>
              )}
            </span>
          </label>
        </div>
      </div>
      {m.isError && <p role="alert" className="text-[12px] text-rose-600">{errMsg(m.error)}</p>}
    </ModalShell>
  );
}

function RenameModal({ role, onClose, onSaved }: { role: RbacRole; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(role.label_cn);
  const [desc, setDesc] = useState(role.description ?? "");
  const m = useMutation({ mutationFn: () => rbacApi.renameRole(role.code, { label_cn: name.trim(), description: desc.trim() }), onSuccess: onSaved });
  return (
    <ModalShell
      title={`改名 · ${role.label_cn}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-[13px]">取消</button>
          <button type="button" data-testid="rename-submit" disabled={!name.trim() || m.isPending} onClick={() => m.mutate()} className="h-9 rounded-lg bg-blue-600 px-5 text-[13px] font-semibold text-white disabled:opacity-50">
            {m.isPending ? "保存中…" : "保存"}
          </button>
        </>
      }
    >
      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-medium text-slate-700">角色名称 *</span>
        <input data-testid="rename-name" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} className={inputCls} />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-medium text-slate-700">说明</span>
        <input value={desc} onChange={(e) => setDesc(e.target.value)} className={inputCls} />
      </label>
      {m.isError && <p role="alert" className="text-[12px] text-rose-600">{errMsg(m.error)}</p>}
    </ModalShell>
  );
}

/** 删除：先试删；还有人在用 → 画面 5「不能删除」并列出是谁；没人用才是普通确认 */
function DeleteModal({ role, holders, onClose, onDeleted }: { role: RbacRole; holders: RoleInUseUser[]; onClose: () => void; onDeleted: () => void }) {
  const [blocked, setBlocked] = useState<RoleInUseUser[] | null>(null);
  const inUse = holders.length > 0 || (role.user_count ?? 0) > 0;
  const m = useMutation({
    mutationFn: () => rbacApi.deleteRole(role.code),
    onSuccess: onDeleted,
    onError: (e) => {
      const d = e instanceof ApiError ? (e.detail as { users?: RoleInUseUser[] } | undefined) : undefined;
      if (e instanceof ApiError && e.status === 409 && d?.users) setBlocked(d.users);
    },
  });
  if (blocked || inUse) {
    const list = blocked ?? holders;
    return (
      <ModalShell
        title={`不能删除「${role.label_cn}」`}
        onClose={onClose}
        footer={
          <>
            <button type="button" onClick={onClose} className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-[13px]">知道了</button>
            <Link to="/admin/permissions" className="flex h-9 items-center rounded-lg bg-blue-600 px-5 text-[13px] font-semibold text-white">去用户管理</Link>
          </>
        }
      >
        <p data-testid="delete-blocked" className="text-[13px] text-slate-700">
          还有 <b>{list.length || role.user_count}</b> 位同事在用这个角色：
          {list.map((u) => <span key={u.id} className="ml-1 font-medium">{u.full_name}（{u.as === "primary" ? "主角色" : "附加角色"}）</span>)}
          。先在「用户管理」里给他们换一个角色，再回来删除。
        </p>
      </ModalShell>
    );
  }
  return (
    <ModalShell
      title={`删除「${role.label_cn}」？`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-[13px]">取消</button>
          <button type="button" data-testid="delete-confirm" disabled={m.isPending} onClick={() => m.mutate()} className="h-9 rounded-lg bg-rose-600 px-5 text-[13px] font-semibold text-white disabled:opacity-50">
            {m.isPending ? "删除中…" : "确认删除"}
          </button>
        </>
      }
    >
      <p className="text-[13px] text-slate-700">删除后这个角色和它勾选的权限都会清掉；同名角色以后可以重新建。操作会记入审计日志。</p>
      {m.isError && !blocked && <p role="alert" className="text-[12px] text-rose-600">{errMsg(m.error)}</p>}
    </ModalShell>
  );
}

function RStat({ icon, tone, label, value, hint, link, linkLabel }: {
  icon: React.ReactNode; tone: "indigo" | "emerald" | "amber" | "slate"; label: string; value: number; hint: string; link?: string; linkLabel?: string;
}) {
  const cls: Record<string, string> = {
    indigo: "bg-indigo-100 text-indigo-600",
    emerald: "bg-emerald-100 text-emerald-600",
    amber: "bg-amber-100 text-amber-600",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${cls[tone]}`}>{icon}</span>
        <span className="text-[12px] text-slate-500">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
      {link ? (
        <Link to={link} className="text-[11px] text-blue-600 hover:underline">{linkLabel ?? hint}</Link>
      ) : (
        <p className="text-[11px] text-slate-400">{hint}</p>
      )}
    </div>
  );
}
